import React, { useEffect, useState } from "react";
import { Button, CircularProgress, Stack, Typography } from "@mui/material";
import { DownloadProgressBar } from "src/ui/components/shared/molecules/DownloadProgressBar";
import {
  DownloadJob,
  getDownloadJobs,
  subscribeDownloadJobs,
} from "src/util/downloadProgress";

const DownloadProgressCard = React.memo(({ job }: { job: DownloadJob }) => {
  const known = job.progress !== null;

  return (
    <Stack
      direction="row"
      spacing={2}
      alignItems="flex-start"
      role="status"
      aria-live="polite"
      sx={(theme) => ({
        width: 340,
        p: 3,
        bgcolor: theme.newColors.neutral.white,
        border: `1px solid ${theme.newColors.neutral.gray200}`,
        borderRadius: theme.spacing(2),
        boxShadow: "0 8px 24px rgba(39, 41, 55, 0.12)",
      })}
    >
      <CircularProgress
        size={36}
        thickness={4}
        variant={known ? "determinate" : "indeterminate"}
        value={job.progress ?? 0}
        sx={(theme) => ({
          color: theme.newColors.theme.primary,
          flexShrink: 0,
          mt: 0.25,
        })}
      />
      <Stack spacing={0.5} sx={{ minWidth: 0, flex: 1 }}>
        <Typography
          variant="subtitle2"
          sx={(theme) => ({ color: theme.newColors.neutral.primary })}
        >
          Downloading document
        </Typography>
        <Typography
          variant="body2"
          noWrap
          title={job.filename}
          sx={(theme) => ({ color: theme.newColors.neutral.gray500 })}
        >
          {job.filename}
        </Typography>
        <DownloadProgressBar progress={job.progress} />
        <Button
          variant="text"
          onClick={job.cancel}
          sx={(theme) => ({
            alignSelf: "flex-start",
            minWidth: 0,
            p: 0,
            textTransform: "none",
            ...theme.typography.subtitle2,
            color: theme.newColors.theme.primary,
            "&:hover": {
              background: "none",
              color: theme.newColors.theme.dark,
            },
          })}
        >
          Cancel
        </Button>
      </Stack>
    </Stack>
  );
});

export const DownloadProgressToast = () => {
  const [jobs, setJobs] = useState<DownloadJob[]>(getDownloadJobs);

  useEffect(
    () =>
      subscribeDownloadJobs(() => {
        setJobs(getDownloadJobs());
      }),
    []
  );

  if (jobs.length === 0) {
    return null;
  }

  return (
    <Stack
      spacing={1.5}
      sx={{
        position: "fixed",
        right: 24,
        bottom: 24,
        zIndex: 1400,
        alignItems: "flex-end",
      }}
    >
      {jobs.map((job) => (
        <DownloadProgressCard key={job.id} job={job} />
      ))}
    </Stack>
  );
};
